import { useCallback, useContext, useState } from 'react';
import { Form, Field } from 'react-final-form';
import { Trans, useLingui } from '@lingui/react/macro';

import Input from 'components/Input';
import Button from 'components/Button';
import Link from 'components/ui/Link';
import * as validation from 'utils/validation';
import { userContext, handleError, LoginData } from 'contexts/UserContext';
import { PasswordChangeRequired, NewPasswordRequired } from 'errors/userErrors';
import { isEmpty } from 'lodash-es';
import { useHistory } from 'react-router';
import { post } from 'utils/http';
import { notification } from 'antd';
import { SUPPORT_LINKS } from 'config';
import { useBrandFeatures } from 'hooks/useBrandFeatures';
import styles from './Form.module.scss';
import StyledForm from './Form';
import { useSetLoginTitle } from './LoginContainer';

interface LoginFormProps {
  className?: string;
  onLogin?: () => void;
}

export default function LoginForm({ className, onLogin }: LoginFormProps) {
  const { login: loginUser } = useContext(userContext);
  const [changePassword, setChangePassword] = useState({});
  const history = useHistory();
  const { t } = useLingui();
  useSetLoginTitle();
  const features = useBrandFeatures();
  const handleLogin = useCallback(
    async ({ login, password, newPassword, ...data }: LoginData) => {
      try {
        await loginUser({ login, password, newPassword, data });
        onLogin?.();
      } catch (e) {
        if (e instanceof NewPasswordRequired) {
          setChangePassword(e.data);
        }
        if (e instanceof PasswordChangeRequired) {
          try {
            await post(
              '/users/auth/forgotpassword',
              { Username: login },
              { params: { action: 'request' } }
            );
            notification.info({
              message: t`Код восстановления выслан на почту`
            });
            history.push(`/recovery/code?login=${login}`);
          } catch (error) {
            handleError(error, t`Ошибка восстановления пароля`);
          }
        }
      }
    },
    [history, loginUser, onLogin, t]
  );

  return (
    <Form
      onSubmit={handleLogin}
      initialValues={changePassword}
      render={({ handleSubmit, submitting, invalid, pristine }) => (
        <StyledForm className={className} onSubmit={handleSubmit}>
          <Field
            name="login"
            validate={validation.validateSignInLogin}
            render={({ input, meta }) => (
              <Input
                disabled={submitting}
                label={t`Логин`}
                {...input}
                error={meta.touched && meta.error}
              />
            )}
          />
          <Field
            name="password"
            validate={validation.required}
            render={({ input, meta }) => (
              <Input
                disabled={submitting}
                label={t`Пароль`}
                type="password"
                {...input}
                error={meta.touched && meta.error}
              />
            )}
          />
          {isEmpty(changePassword) || (
            <Field
              name="newPassword"
              validate={validation.password}
              render={({ input, meta }) => (
                <Input
                  disabled={submitting}
                  label={t`Новый пароль`}
                  type="password"
                  {...input}
                  error={meta.touched && meta.error}
                />
              )}
            />
          )}
          <div className={styles.demoInfo}>
            <Trans>
              Для demo-доступа используйте <code>demo</code> в качестве логина и
              любой пароль
            </Trans>
          </div>
          <Button type="submit" disabled={invalid || submitting || pristine}>
            {t`Войти`}
          </Button>
          <Link to="/recovery">{t`Восстановить`}</Link>
          {features.supportInfo && (
            <div className={styles.hint}>
              <Trans>
                <p>
                  Для получения аккаунта в системе GSM мониторинга вам
                  необходимо обратиться в службу технической поддержки по
                  следующим каналам:
                </p>
                <p>
                  <a href={SUPPORT_LINKS.TG}>{SUPPORT_LINKS.TG}</a>
                </p>
                <p>
                  <a href={`tel:${SUPPORT_LINKS.Tell}`}>{SUPPORT_LINKS.Tell}</a>
                </p>
                <p>
                  Техническая поддержка работает ежедневно, с 7:00 до 21:00 по
                  Московскому времени. Оформление доступа в личный кабинет
                  происходит <b>ТОЛЬКО</b> через переписку!
                </p>
              </Trans>
            </div>
          )}
        </StyledForm>
      )}
    />
  );
}
